import { useCallback, useRef } from 'react';

import { ApolloClient, DocumentNode, QueryOptions, TypedDocumentNode } from '@apollo/client';
import {
  LazyQueryHookOptions,
  LazyQueryResultTuple,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client/react/types/types';

type SimpleCacheQueryResult<TData, TVariables> = Pick<
  QueryResult<TData, TVariables>,
  'loading' | 'data' | 'previousData' | 'refetch' | 'error' | 'called'
>;

/** @allowFromCache - true: if the query result is stored in the cache (by input variables) return it - false: always fetch fresh  */
type SimpleLazyQueryHookOptions<TData, TVariables> = Omit<
  Partial<LazyQueryHookOptions<TData, TVariables>>,
  'fetchPolicy'
> & { allowFromCache: boolean; TTL?: number };

type SimpleLazyQueryResultTuple<TData, TVariables> = [
  (
    options: SimpleLazyQueryHookOptions<TData, TVariables>
  ) => Promise<SimpleCacheQueryResult<TData, TVariables>>,
  SimpleCacheQueryResult<TData, TVariables>
];

type Result = { data: any; error: any; timestamp: number };

// 5 minutes
const DEFAULT_TTL = 5 * 60 * 1000;

// map[hookFunction][GQL variables stringified] = { data, error, timestamp }
const hookFunctionToArgsToResultMap = new Map<Function, Map<string, Result>>();

// allow some strong typing for the baseOptions
function getIsLazyQueryAssertion(
  baseOptions: any,
  isLazyQuery: boolean
): baseOptions is SimpleLazyQueryHookOptions<any, any> {
  return isLazyQuery;
}

// allow both useQuery/useLazyQuery to use nearly same code at the expense of some minimal type safety
const useSimpleCacheBase = (
  func: any,
  baseOptions:
    | (QueryHookOptions<any, any> & { TTL?: number })
    | SimpleLazyQueryHookOptions<any, any> = {} as SimpleLazyQueryHookOptions<any, any>,
  isLazyQuery: boolean
): SimpleCacheQueryResult<any, any> => {
  const isLazyQueryAssertion = getIsLazyQueryAssertion(baseOptions, isLazyQuery);
  const fetchTimeVariablesRef = useRef<object>(undefined);

  const getVariablesString = () =>
    JSON.stringify({
      ...baseOptions.variables,
      ...fetchTimeVariablesRef.current,
    });

  const getCacheEntry = () => {
    const functionInMap = hookFunctionToArgsToResultMap.get(func);
    const variablesString = getVariablesString();
    const entry = functionInMap?.get(variablesString);

    if (!entry?.timestamp) {
      // Remove entry without timestamp
      functionInMap?.delete(variablesString);
      return undefined;
    }

    const timeDifference = Date.now() - entry.timestamp;
    const ttl = baseOptions?.TTL ?? DEFAULT_TTL;

    if (timeDifference > ttl) {
      // Remove expired cache entry
      functionInMap?.delete(variablesString);
      return undefined;
    }

    return entry;
  };

  // create cache entry if it doesn't exist for use later
  const assertCacheEntry: () => Result = () => {
    if (!hookFunctionToArgsToResultMap.has(func)) {
      hookFunctionToArgsToResultMap.set(func, new Map());
    }

    const functionMap = hookFunctionToArgsToResultMap.get(func);
    const variablesString = getVariablesString();

    if (!functionMap!.has(variablesString)) {
      functionMap!.set(variablesString, {} as Result);
    }

    const result = functionMap!.get(variablesString);

    return result as Result;
  };

  const renderCacheEntry = getCacheEntry();

  const finalOptions = {
    ...baseOptions,
    // this prevents apollo from using it's slow / complicated cache which is the whole point of this wrapper
    fetchPolicy: 'no-cache',
    skip: (!isLazyQueryAssertion && baseOptions?.skip) || !!renderCacheEntry,
    onCompleted: (data: any) => {
      const cache = assertCacheEntry();
      cache.data = data;
      cache.timestamp = Date.now();
      baseOptions.onCompleted?.(data);
    },
    onError: (error: any) => {
      const cache = assertCacheEntry();
      cache.error = error;
      cache.timestamp = Date.now();
      baseOptions.onError?.(error);
    },
  };

  const funcResult = func(finalOptions);

  const finalResult = isLazyQueryAssertion ? funcResult[1] : funcResult;
  const fetch = isLazyQueryAssertion ? funcResult[0] : finalResult.refetch;

  // Apollo's useLazyQuery `execute` function (`fetch` above) never changes reference, so
  // `useCallback(fn, [fetch])` would only ever run once, permanently closing over that render's
  // `finalOptions`/`baseOptions` (e.g. a caller's `onCompleted` capturing render-scoped state).
  // Reading the latest implementation via a ref (updated every render) keeps `wrappedFetch`
  // itself stable while ensuring it always executes against the current render's options.
  const latestFetchImplRef = useRef<(fetchOptions?: SimpleLazyQueryHookOptions<any, any>) => any>(
    undefined
  );
  latestFetchImplRef.current = (fetchOptions: SimpleLazyQueryHookOptions<any, any> = {} as any) => {
    fetchTimeVariablesRef.current = fetchOptions?.variables;
    const cacheEntry = getCacheEntry();

    // serve from cache on lazy queries if we have it and its allowed
    if (isLazyQueryAssertion && fetchOptions?.allowFromCache && cacheEntry?.data) {
      finalOptions.onCompleted?.(cacheEntry?.data);
      fetchOptions.onCompleted?.(cacheEntry.data);
      return Promise.resolve(cacheEntry.data);
    }

    // apollo's lazyQueries unexpectedly don't call base queryOptions onCompleted/onError if fetch has it specified. normalize this confusing behavior.
    if (isLazyQueryAssertion) {
      const origOnCompleted = fetchOptions.onCompleted;
      fetchOptions.onCompleted = data => {
        origOnCompleted?.(data);
        finalOptions?.onCompleted(data);
      };
      const origOnError = fetchOptions.onError;
      fetchOptions.onError = data => {
        origOnError?.(data);
        finalOptions?.onError(data);
      };
    }
    return fetch(fetchOptions);
  };

  const wrappedFetch = useCallback(
    (fetchOptions?: SimpleLazyQueryHookOptions<any, any>) =>
      latestFetchImplRef.current!(fetchOptions),
    []
  );

  return {
    error: finalResult.error || renderCacheEntry?.error,
    refetch: wrappedFetch,
    loading: finalResult.loading,
    data: finalResult.data || renderCacheEntry?.data,
    previousData: finalResult.loading ? renderCacheEntry : null,
    called: finalResult.called || !!renderCacheEntry,
  };
};

/**
 * Wrap useQuery with a simple cache that doesn't have performance issues like apollo's does
 * @param useQueryHook - the original useQuery from apollo
 * @returns useQuery wrapped with simple cache
 */
export const withSimpleCache = <TData, TVariables>(
  useQueryHook: (
    baseOptions: QueryHookOptions<TData, TVariables> & { TTL?: number }
  ) => QueryResult<TData, TVariables>
): ((
  baseOptions?: Omit<QueryHookOptions<TData, TVariables>, 'fetchPolicy'> & { TTL?: number }
) => SimpleCacheQueryResult<TData, TVariables>) => baseOptions => {
  return useSimpleCacheBase(useQueryHook, baseOptions, false);
};

/**
 * Wrap useLazyQuery with a simple cache that doesn't have performance issues like apollo's does
 * @param useLazyQueryHook - the original useLazyQuery from apollo
 * @returns useLazyQuery wrapped with simple cache
 */
export const withLazySimpleCache = <TData, TVariables>(
  useLazyQueryHook: (
    baseOptions?: Omit<LazyQueryHookOptions<TData, TVariables>, 'fetchPolicy'>
  ) => LazyQueryResultTuple<TData, TVariables>
): ((
  baseOptions?: Omit<LazyQueryHookOptions<TData, TVariables>, 'fetchPolicy'>
) => SimpleLazyQueryResultTuple<TData, TVariables>) => baseOptions => {
  const result = useSimpleCacheBase(useLazyQueryHook, baseOptions, true);
  return [result.refetch as any, result];
};

// map[GQL AST][GQL variables stringified] = { data, timestamp }
const astToArgsToResultMap = new Map<
  DocumentNode | TypedDocumentNode<any, any>,
  Map<string, { data: any; timestamp: number }>
>();

/**
 * make a GQL network query using simple cache if the data is already cached instead of apollo's cache which has performance issues
 * @param client - apollo client
 * @param baseOptions - client options
 * @returns promise of data via a network call or from the cache
 */
export const simpleCacheQuery = async <TVariables, TData = any>(
  client: ApolloClient<any>,
  baseOptions: Omit<QueryOptions<TVariables, TData>, 'fetchPolicy'> & { TTL?: number }
): Promise<TData> => {
  if (!astToArgsToResultMap.has(baseOptions.query)) {
    astToArgsToResultMap.set(baseOptions.query, new Map());
  }

  const keyInMap = astToArgsToResultMap.get(baseOptions.query);
  const variablesString = JSON.stringify(baseOptions.variables);
  const cachedResult = keyInMap?.get(variablesString);

  if (cachedResult?.timestamp) {
    const timeDifference = Date.now() - cachedResult.timestamp;
    const ttl = baseOptions?.TTL ?? DEFAULT_TTL;

    // Only return cached data if it's not expired
    if (timeDifference <= ttl) {
      return Promise.resolve(cachedResult.data);
    }
  }

  // Fetch fresh data if no cache is available or expired
  const result = await client.query({ ...baseOptions, fetchPolicy: 'no-cache' });

  keyInMap?.set(variablesString, { data: result.data, timestamp: Date.now() });

  return result.data;
};
